/*
  bfroz-video.js : chargement différé des médias lourds de l'accueil + repli animé quand iOS refuse la lecture.

  ============================================================================================
  MODE ÉCONOMIE D'ÉNERGIE (iOS) — ce que ce fichier fait et ne fait pas
  ============================================================================================
  Apple bloque TOUTE lecture automatique de <video> quand l'iPhone est en économie d'énergie, même muette,
  même inline. Le rejet vient du système : play() renvoie une promesse rejetée (NotAllowedError) avant que
  le JS puisse intervenir. Aucun contournement n'existe. Ne cherche pas : il n'y en a pas.

  Stratégie retenue, dans cet ordre :
    1. Lecture normale de la vidéo. Marche partout sauf en économie d'énergie.
    2. Si refusée → on affiche `data-lowpower-src` : un WebP ANIMÉ. iOS anime les images sans aucune
       restriction d'alimentation. Visuellement, l'utilisateur voit une boucle animée, pas un trou noir.
    3. Si pas de WebP animé fourni → on affiche l'affiche fixe (poster).
    4. Dans les cas 2 et 3, le premier contact de l'utilisateur (n'importe où sur la page) démarre la vraie
       vidéo : un geste utilisateur lève le blocage, même en économie d'énergie.

  MARKUP ATTENDU :
    <video data-video-lazy
           poster="…/hero-poster.jpg"
           data-lowpower-src="…/hero-loop.webp"   (facultatif mais recommandé)
           data-lowpower-hint="1"                  (facultatif : petit repère « toucher pour lire »)
           muted playsinline preload="none">
      <source src="…/hero.mp4" type="video/mp4">
    </video>

  FABRIQUER LE WEBP ANIMÉ (vise 3–6 s de boucle, largeur 720, sous 800 Ko) :
    ffmpeg -i hero.mp4 -t 5 -vf "fps=15,scale=720:-2" -loop 0 -q:v 55 -compression_level 6 hero-loop.webp

  ============================================================================================
  Correctifs iOS conservés
  ============================================================================================
    a. load() explicite : changer `preload` sur un <video> déjà inséré ne déclenche aucun téléchargement.
    b. muted / playsinline posés EN PROPRIÉTÉ avant play(), pas seulement en attribut.
    c. reprise après le cache page-arrière et le retour d'arrière-plan.
    d. sources non-MP4 (HLS .m3u8) retirées : WebKit les choisit en premier, Blink les ignore.

  DEUX VERROUS DE CHARGEMENT DIFFÉRÉ :
    1. L'IntersectionObserver décide SI on télécharge. Sa marge doit rester PLUS PETITE que l'écart réel
       entre le bas de la fenêtre et le haut de la section (449 px mobile 375x812, 516 px desktop 1440x900).
    2. `apresChargement` décide QUAND AU PLUS TÔT : jamais avant load + un moment d'inactivité.

  DIAGNOSTIC : ajouter ?bfrozdebug=1 à l'URL. Encart d'état en bas de l'écran.
*/
(function () {
  'use strict';

  var DEBUG = /[?&]bfrozdebug=1/.test(window.location.search);

  /* ---------------- diagnostic ---------------- */

  var boite = null;
  function tracer(msg) {
    if (!DEBUG) return;
    if (!boite) {
      boite = document.createElement('div');
      boite.setAttribute('style', [
        'position:fixed', 'left:0', 'right:0', 'bottom:0', 'z-index:2147483647',
        'max-height:45vh', 'overflow:auto', 'background:rgba(0,0,0,.88)', 'color:#0f0',
        'font:11px/1.45 ui-monospace,Menlo,monospace', 'padding:8px 10px', 'white-space:pre-wrap'
      ].join(';'));
      (document.body || document.documentElement).appendChild(boite);
    }
    boite.appendChild(document.createTextNode(msg + '\n'));
    boite.scrollTop = boite.scrollHeight;
  }

  function etat(video, etiquette) {
    if (!DEBUG) return;
    tracer(etiquette +
      ' | err=' + (video.error ? video.error.code : 'aucune') +
      ' | ready=' + video.readyState +
      ' | ' + video.videoWidth + 'x' + video.videoHeight +
      ' | paused=' + video.paused +
      ' | muted=' + video.muted);
  }

  /* ---------------- repli visuel (économie d'énergie) ---------------- */

  // Le WebP animé est posé en fond de l'élément <video>. Pas d'insertion dans le DOM, donc aucun risque
  // de casser la mise en page ou les styles existants : l'élément garde sa taille et son object-fit.
  // Il faut retirer `poster`, sinon l'affiche fixe recouvre le fond animé.
  function afficherRepli(video) {
    if (video.__bfrozRepli) return;
    video.__bfrozRepli = true;

    var anime = video.getAttribute('data-lowpower-src');
    var fixe = video.getAttribute('poster');
    var image = anime || fixe;
    if (!image) {
      tracer('aucun repli disponible (ni data-lowpower-src ni poster)');
      return;
    }

    if (anime) {
      video.__bfrozPosterSauve = fixe || '';
      video.removeAttribute('poster');
    }

    var ajustement = 'cover';
    try {
      var calc = window.getComputedStyle(video).objectFit;
      if (calc === 'contain' || calc === 'fill') ajustement = calc === 'fill' ? '100% 100%' : 'contain';
    } catch (e) { /* style non lisible : on garde cover */ }

    video.style.backgroundImage = 'url("' + image + '")';
    video.style.backgroundSize = ajustement;
    video.style.backgroundPosition = 'center';
    video.style.backgroundRepeat = 'no-repeat';
    tracer('repli affiche : ' + (anime ? 'WebP anime' : 'affiche fixe'));

    if (video.getAttribute('data-lowpower-hint') === '1') poserRepere(video);
  }

  function retirerRepli(video) {
    video.style.backgroundImage = '';
    if (video.__bfrozPosterSauve) video.setAttribute('poster', video.__bfrozPosterSauve);
    if (video.__bfrozRepere && video.__bfrozRepere.parentNode) {
      video.__bfrozRepere.parentNode.removeChild(video.__bfrozRepere);
      video.__bfrozRepere = null;
    }
  }

  // Repère discret « toucher pour lire », opt-in via data-lowpower-hint="1".
  // Positionné dans le parent de la vidéo : celui-ci doit être en position relative/absolute/fixed.
  function poserRepere(video) {
    var parent = video.parentNode;
    if (!parent || video.__bfrozRepere) return;
    try {
      if (window.getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
    } catch (e) { return; }

    var d = document.createElement('div');
    d.setAttribute('aria-hidden', 'true');
    d.setAttribute('style', [
      'position:absolute', 'left:50%', 'top:50%', 'transform:translate(-50%,-50%)',
      'width:64px', 'height:64px', 'border-radius:50%', 'pointer-events:none',
      'background:rgba(255,255,255,.22)', '-webkit-backdrop-filter:blur(6px)', 'backdrop-filter:blur(6px)',
      'display:flex', 'align-items:center', 'justify-content:center', 'z-index:2'
    ].join(';'));
    d.innerHTML = '<svg width="22" height="26" viewBox="0 0 22 26" fill="#fff" aria-hidden="true">' +
                  '<path d="M0 0l22 13L0 26z"/></svg>';
    parent.appendChild(d);
    video.__bfrozRepere = d;
  }

  /* ---------------- forcer le MP4 (WebKit choisit le HLS en premier) ---------------- */

  function estMp4(url, type) {
    if (type && type.indexOf('mp4') !== -1) return true;
    if (type && type.toLowerCase().indexOf('mpegurl') !== -1) return false;
    return /\.mp4(\?|$)/i.test(url || '');
  }

  function forcerMp4(video) {
    if (video.__bfrozMp4) return;
    video.__bfrozMp4 = true;
    var sources = video.querySelectorAll('source');
    if (!sources.length) return;
    var choisi = null, aRetirer = [];
    for (var i = 0; i < sources.length; i++) {
      var url = sources[i].getAttribute('src');
      var type = sources[i].getAttribute('type') || '';
      if (estMp4(url, type)) { if (!choisi) choisi = url; }
      else aRetirer.push(sources[i]);
    }
    if (!choisi) return;
    for (var j = 0; j < aRetirer.length; j++) aRetirer[j].parentNode.removeChild(aRetirer[j]);
    if (video.getAttribute('src') !== choisi) video.setAttribute('src', choisi);
  }

  /* ---------------- préparation iOS ---------------- */

  function preparerPourIOS(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('disableremoteplayback', '');
  }

  function chargerSiVide(video) {
    if (video.readyState >= 1) return;
    try { video.load(); } catch (e) { /* rien de plus à tenter */ }
  }

  /* ---------------- lecture ---------------- */

  // Un geste utilisateur lève le blocage même en économie d'énergie. On écoute le premier contact
  // n'importe où sur la page — l'utilisateur n'a pas à viser la vidéo.
  function armerGeste(video) {
    if (video.__bfrozGeste) return;
    video.__bfrozGeste = true;
    chargerSiVide(video);
    var go = function () {
      document.removeEventListener('touchend', go, false);
      document.removeEventListener('click', go, false);
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.then(function () {
          tracer('lecture demarree par geste utilisateur');
          retirerRepli(video);
        }, function (err) {
          etat(video, 'refus meme apres geste : ' + err.name);
        });
      }
    };
    document.addEventListener('touchend', go, false);
    document.addEventListener('click', go, false);
  }

  function tenterLecture(video, reessayer) {
    var p;
    try { p = video.play(); } catch (e) { afficherRepli(video); return armerGeste(video); }
    if (!p || typeof p.catch !== 'function') return;

    p.then(function () {
      etat(video, 'play() accepte');
      retirerRepli(video);
    }, function (err) {
      etat(video, 'play() rejete : ' + err.name);

      // NotAllowedError = économie d'énergie, ou lecture auto désactivée dans les réglages Safari.
      // C'est le cas qui nous intéresse : on bascule tout de suite sur le repli animé.
      if (err.name === 'NotAllowedError') {
        afficherRepli(video);
        return armerGeste(video);
      }

      if (reessayer) {
        var once = function () {
          video.removeEventListener('canplay', once, false);
          tenterLecture(video, false);
        };
        video.addEventListener('canplay', once, false);
        window.setTimeout(function () {
          if (video.paused) { afficherRepli(video); armerGeste(video); }
        }, 1500);
      } else {
        afficherRepli(video);
        armerGeste(video);
      }
    });
  }

  function start(video) {
    if (video.__bfrozStarted) return;
    video.__bfrozStarted = true;
    preparerPourIOS(video);
    video.preload = 'auto';
    video.setAttribute('preload', 'auto');
    chargerSiVide(video);
    tenterLecture(video, true);

    // Filet : rien de décodé au bout de 6 s, on montre le repli plutôt qu'un cadre vide.
    window.setTimeout(function () {
      if (video.paused || video.readyState < 2 || video.videoWidth === 0) {
        etat(video, 'rien apres 6 s');
        afficherRepli(video);
        armerGeste(video);
      }
    }, 6000);
  }

  function apresChargement(fn) {
    var fait = false;
    function unefois() {
      if (fait) return;
      fait = true;
      if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 2000 });
      else window.setTimeout(fn, 300);
    }
    if (document.readyState === 'complete') return unefois();
    window.addEventListener('load', unefois);
    window.setTimeout(unefois, 2500);
  }

  function surveillerReprise(video) {
    var reprendre = function () {
      if (!video.__bfrozStarted || !video.paused || video.ended) return;
      var r = video.getBoundingClientRect();
      if (r.bottom <= 0 || r.top >= (window.innerHeight || 0)) return;
      var p = video.play();
      if (p && typeof p.then === 'function') {
        p.then(function () { retirerRepli(video); }, function () {});
      }
    };
    window.addEventListener('pageshow', reprendre, false);
    document.addEventListener('visibilitychange', function () { if (!document.hidden) reprendre(); }, false);
  }

  function init(video) {
    if (video.__bfrozLazy) return;
    video.__bfrozLazy = true;
    forcerMp4(video);
    preparerPourIOS(video);
    surveillerReprise(video);
    video.addEventListener('error', function () { afficherRepli(video); }, false);

    if (!video.getAttribute('poster') && video.preload === 'none') {
      video.preload = 'metadata';
      video.setAttribute('preload', 'metadata');
    }

    if (!('IntersectionObserver' in window)) return apresChargement(function () { start(video); });

    var margin = parseInt(video.getAttribute('data-video-margin'), 10);
    if (isNaN(margin) || margin < 0) margin = 200;
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          io.disconnect();
          apresChargement(function () { start(video); });
          return;
        }
      }
    }, { rootMargin: margin + 'px 0px ' + margin + 'px 0px' });
    io.observe(video);
  }

  /* ---------------- images différées d'un groupe (grille des vibes) ---------------- */

  function poser(groupe) {
    var imgs = groupe.querySelectorAll('img[data-defer-src]');
    for (var i = 0; i < imgs.length; i++) {
      var src = imgs[i].getAttribute('data-defer-src');
      if (!src) continue;
      imgs[i].removeAttribute('data-defer-src');
      imgs[i].setAttribute('src', src);
    }
  }

  function initGroupe(groupe) {
    if (groupe.__bfrozDefer) return;
    groupe.__bfrozDefer = true;
    var lancer = function () { apresChargement(function () { poser(groupe); }); };
    if (!('IntersectionObserver' in window)) return lancer();
    var margin = parseInt(groupe.getAttribute('data-defer-margin'), 10);
    if (isNaN(margin) || margin < 0) margin = 400;
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { io.disconnect(); lancer(); return; }
      }
    }, { rootMargin: margin + 'px 0px ' + margin + 'px 0px' });
    io.observe(groupe);
  }

  function initAll(root) {
    var base = root || document;
    var videos = base.querySelectorAll('video[data-video-lazy]');
    for (var i = 0; i < videos.length; i++) init(videos[i]);
    var groupes = base.querySelectorAll('[data-defer-group]');
    for (var j = 0; j < groupes.length; j++) initGroupe(groupes[j]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelectorAll) initAll(e.target);
  });
})();